import { z } from 'zod';
import { isoTimestampSchema, outcomeSchema } from './common.js';
/**
 * Bookmark types as defined by the Admin Portal. The distinction matters to the
 * test because each type surfaces in a different Salesforce component:
 * `Bookmark` and `ActionEngine` render in rpLiveBookmarks, while `CallAssist`
 * renders in rpCallAssistComponent.
 */
export const bookmarkTypeSchema = z.enum(['Bookmark', 'CallAssist', 'ActionEngine']);
/** Which component a triggered bookmark of each type is expected to appear in. */
export const BOOKMARK_TARGET_COMPONENT = {
    Bookmark: 'rpLiveBookmarks',
    ActionEngine: 'rpLiveBookmarks',
    CallAssist: 'rpCallAssistComponent',
};
/**
 * In-call intelligence the operator can include or leave out of a live-call
 * run. Connecting and hanging up is always in scope; these flags only control
 * the panels that need a conversation, tenant configuration, or extra secrets.
 *
 * Bookmarks and checklist items are the v2 entities the PBX backend uses when
 * it processes a call. Older v1 lists are not planted or asserted against.
 */
export const CALL_FEATURE_IDS = [
    'live-transcript',
    'live-bookmarks',
    'call-assist',
    'question-assist',
    'rocket-form',
    'checklist',
];
export const CALL_FEATURE_LABELS = {
    'live-transcript': 'Live Transcript',
    'live-bookmarks': 'Live Bookmarks',
    'call-assist': 'Call Assist',
    'question-assist': 'Question Assist',
    'rocket-form': 'RocketForm',
    checklist: 'Checklist',
};
export const CALL_FEATURE_HINTS = {
    'live-transcript': 'Watch customer and agent speech appear in the transcript panel.',
    'live-bookmarks': 'Plant v2 bookmark phrases and watch Live Bookmarks react.',
    'call-assist': 'Plant Call Assist phrases and watch the Call Assist panel.',
    'question-assist': 'Ask questions and watch Smart Question Detect.',
    'rocket-form': 'Open a RocketForm and check it fills from the conversation.',
    checklist: 'Plant v2 checklist phrases and watch items tick off.',
};
export const callFeaturesSchema = z.object({
    'live-transcript': z.boolean().default(true),
    'live-bookmarks': z.boolean().default(true),
    'call-assist': z.boolean().default(true),
    'question-assist': z.boolean().default(true),
    'rocket-form': z.boolean().default(true),
    checklist: z.boolean().default(true),
});
export const DEFAULT_CALL_FEATURES = {
    'live-transcript': true,
    'live-bookmarks': true,
    'call-assist': true,
    'question-assist': true,
    'rocket-form': true,
    checklist: true,
};
/** Fills any omitted flags with the current defaults (all on). */
export function resolveCallFeatures(partial) {
    return callFeaturesSchema.parse(partial ?? {});
}
export function anyCallFeatureEnabled(features, ids) {
    return ids.some((id) => features[id]);
}
/**
 * Live Bookmarks and Call Assist phrases come from the Admin Portal's v2
 * bookmark list. Checklist phrases come from the gateway v2 checklist, so they
 * do not need the portal.
 */
export function needsPortalForCall(features) {
    return features['live-bookmarks'] || features['call-assist'];
}
/** The agent's v2 checklist is read from the gateway, not the Admin Portal. */
export function needsGatewayForCall(features) {
    return features.checklist;
}
/**
 * Anything that needs the voice bot: planted phrases, questions, forms, or a
 * live transcript to watch. A connectivity-only run skips all of this.
 */
export function needsVoiceForCall(features) {
    return anyCallFeatureEnabled(features, CALL_FEATURE_IDS);
}
/** In-call panels render on a Contact; connectivity-only can dial from Home. */
export function needsInCallRecordPage(features) {
    return needsVoiceForCall(features);
}
/** V2 bookmark types that should be planted for the selected features. */
export function bookmarkTypesForCall(features) {
    const types = [];
    if (features['live-bookmarks']) {
        types.push('Bookmark', 'ActionEngine');
    }
    if (features['call-assist']) {
        types.push('CallAssist');
    }
    return types;
}
/**
 * A V2 bookmark as returned by the Admin Portal. The portal's field for trigger
 * phrases is `triggers`; the UI labels it "Hints, Keywords and Phrases".
 * Unknown fields are preserved so a portal release cannot break parsing.
 */
export const bookmarkSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    bookmarkType: bookmarkTypeSchema.catch('Bookmark'),
    triggeredBy: z.string().nullish(),
    captureLimit: z.number().nullish(),
    content: z.string().nullish(),
    triggers: z.array(z.string()).default([]),
    isArchived: z.boolean().nullish(),
    isFavorite: z.boolean().nullish(),
    visibility: z.string().nullish(),
    actions: z.array(z.string()).nullish(),
    teamId: z.string().nullish(),
})
    .passthrough();
/**
 * A checklist item sourced from the gateway checklist endpoint, which is the
 * same data rpCheckListForInCall renders. Field names vary between responses so
 * the schema accepts the common aliases and normalises downstream.
 */
export const checklistItemSchema = z
    .object({
    id: z.string().nullish(),
    name: z.string().nullish(),
    title: z.string().nullish(),
    description: z.string().nullish(),
    bookmarkId: z.string().nullish(),
    bookmarkName: z.string().nullish(),
    triggers: z.array(z.string()).nullish(),
})
    .passthrough();
export const checklistSchema = z
    .object({
    id: z.string().nullish(),
    name: z.string().nullish(),
    items: z.array(checklistItemSchema).default([]),
})
    .passthrough();
/**
 * One phrase the voice bot must say, together with what should happen in
 * Salesforce afterwards. This is the contract between the conversation planner
 * and the in-call assertions: the planner guarantees the phrase is spoken, the
 * assertions watch for the corresponding UI reaction.
 */
export const plannedTriggerSchema = z.object({
    id: z.string(),
    phrase: z.string(),
    kind: z.enum(['Bookmark', 'CallAssist', 'ActionEngine', 'ChecklistItem']),
    sourceId: z.string(),
    sourceName: z.string(),
    /** Component expected to react, used to pick the assertion strategy. */
    expectedComponent: z.string(),
    /** Who must say it; customer-side lines are voiced by the bot's persona. */
    speaker: z.enum(['agent', 'customer']).default('customer'),
});
export const conversationPlanSchema = z.object({
    /** Scenario prompt handed to the Gemini Live sidecar. */
    persona: z.string(),
    /** Ordered lines the bot works through during the call. */
    beats: z.array(z.object({
        order: z.number().int().nonnegative(),
        instruction: z.string(),
        triggerIds: z.array(z.string()).default([]),
    })),
    triggers: z.array(plannedTriggerSchema),
    /** Questions asked from the customer side to exercise Smart Question Detect. */
    questions: z.array(z.string()).default([]),
});
export const transcriptSegmentSchema = z.object({
    speaker: z.enum(['agent', 'customer', 'system']),
    text: z.string(),
    at: isoTimestampSchema,
});
export const triggerObservationSchema = z.object({
    triggerId: z.string(),
    phrase: z.string(),
    kind: z.enum(['Bookmark', 'CallAssist', 'ActionEngine', 'ChecklistItem']),
    spokenAt: isoTimestampSchema,
    observedAt: isoTimestampSchema.optional(),
    observed: z.boolean(),
    latencyMs: z.number().int().nonnegative().optional(),
    outcome: outcomeSchema,
});
/** Summary of a completed call, assembled for the post-call stage. */
export const callSummarySchema = z.object({
    callId: z.string().optional(),
    taskId: z.string().optional(),
    taskUrl: z.string().optional(),
    agentNumber: z.string().optional(),
    destinationNumber: z.string().optional(),
    startedAt: isoTimestampSchema.optional(),
    endedAt: isoTimestampSchema.optional(),
    durationSeconds: z.number().int().nonnegative().optional(),
    transcript: z.array(transcriptSegmentSchema).default([]),
    triggers: z.array(triggerObservationSchema).default([]),
});
//# sourceMappingURL=call.js.map